import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Pencil, FileDown, Printer } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { generateInvoicePdf } from "../../lib/invoicePdf";
import { toast } from "sonner";
import { WhatsAppShareButton } from "../../components/WhatsAppShareButton";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

export default function MyOrders() {
  const [items, setItems] = useState([]);
  const [storeMap, setStoreMap] = useState(new Map());
  const [sel, setSel] = useState(null);

  useEffect(() => {
    api.get("/orders").then((r) => setItems(r.data));
    api.get("/stores").then((r) => {
      const m = new Map();
      for (const s of r.data) m.set(s.id, s);
      setStoreMap(m);
    });
  }, []);

  return (
    <div className="space-y-4" data-testid="my-orders-page">
      <div>
        <p className="overline text-[color:var(--muted)]">History</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">My orders</h1>
      </div>

      {items.length === 0 && (
        <div className="text-center py-12 text-[color:var(--muted)] rounded-xl border border-dashed border-[color:var(--border)]">
          No orders yet. Start your route!
        </div>
      )}

      <div className="space-y-2">
        {items.map((o) => {
          const s = storeMap.get(o.store_id);
          return (
            <button
              key={o.id}
              onClick={() => setSel(o)}
              className="w-full text-left p-4 rounded-xl border border-[color:var(--border)] bg-white hover:bg-[color:var(--surface-2)]/50"
              data-testid={`my-order-${o.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{o.store_name}</div>
                  {s?.address && (
                    <div className="text-xs text-[color:var(--muted)] truncate">{s.address}</div>
                  )}
                  {s?.phone && (
                    <div className="text-xs text-[color:var(--primary)] truncate">📞 {s.phone}</div>
                  )}
                  <div className="text-xs text-[color:var(--muted)] mt-0.5">
                    {new Date(o.created_at).toLocaleString()} · {o.items.length} items
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display font-bold">{fmtINR(o.total)}</div>
                  <div className="flex items-center gap-1 justify-end mt-0.5">
                    <span
                      className={`text-[9px] px-1.5 py-0.5 rounded-full uppercase tracking-wide font-semibold ${
                        o.payment_status === "paid"
                          ? "bg-[color:var(--moss)]/15 text-[color:var(--moss)]"
                          : "bg-[color:var(--mustard)]/20 text-[#9A742E]"
                      }`}
                    >
                      {o.payment_status === "paid" ? "Paid" : "Credit"}
                    </span>
                    <span className="text-[10px] text-[color:var(--muted)] uppercase tracking-wide">
                      {o.store_type}
                    </span>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Order details</DialogTitle>
          </DialogHeader>
          {sel && (
            <div className="space-y-3">
              <div>
                <div className="font-display text-lg font-bold">{sel.store_name}</div>
                {(() => {
                  const s = storeMap.get(sel.store_id);
                  if (!s) return null;
                  return (
                    <div className="text-xs text-[color:var(--muted)] mt-0.5 space-y-0.5">
                      {s.address && <div>{s.address}</div>}
                      {s.phone && (
                        <a
                          href={`tel:${s.phone}`}
                          className="text-[color:var(--primary)] inline-block"
                        >
                          📞 {s.phone}
                        </a>
                      )}
                    </div>
                  );
                })()}
                <div className="text-xs text-[color:var(--muted)] mt-1">
                  {new Date(sel.created_at).toLocaleString()}
                  {sel.updated_at && (
                    <span className="ml-2 italic">· edited {new Date(sel.updated_at).toLocaleString()}</span>
                  )}
                </div>
              </div>
              <ul className="divide-y divide-[color:var(--border)]">
                {sel.items.map((it) => (
                  <li key={it.product_id} className="py-2 flex items-center justify-between text-sm">
                    <div className="font-tamil">{it.product_name} × {it.quantity}</div>
                    <div>{fmtINR(it.line_total)}</div>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between border-t border-[color:var(--border)] pt-3">
                <span className="font-display">Total</span>
                <span className="font-display text-xl font-bold">{fmtINR(sel.total)}</span>
              </div>
              <Button
                asChild
                className="w-full h-12 bg-[color:var(--ink)] hover:bg-black text-white"
                data-testid={`edit-order-link-${sel.id}`}
              >
                <Link to={`/app/orders/${sel.id}/edit`}>
                  <Pencil className="h-4 w-4 mr-2" /> Edit order
                </Link>
              </Button>
              <WhatsAppShareButton
                order={sel}
                store={storeMap.get(sel.store_id)}
                className="w-full h-12"
              />
              <Button
                onClick={async () => {
                  try {
                    await generateInvoicePdf(sel, storeMap.get(sel.store_id), {
                      output: "save",
                    });
                    toast.success("Invoice PDF downloaded");
                  } catch (e) {
                    toast.error("Could not generate invoice");
                  }
                }}
                variant="outline"
                className="w-full h-12 border-[color:var(--ink)] text-[color:var(--ink)]"
                data-testid={`download-invoice-${sel.id}`}
              >
                <FileDown className="h-4 w-4 mr-2" /> Download invoice PDF
              </Button>
              <Button
                onClick={async () => {
                  try {
                    await generateInvoicePdf(
                      sel,
                      storeMap.get(sel.store_id),
                      { output: "print" },
                    );
                  } catch (e) {
                    toast.error("Could not open invoice for printing");
                  }
                }}
                variant="outline"
                className="w-full h-12"
                data-testid={`print-invoice-${sel.id}`}
              >
                <Printer className="h-4 w-4 mr-2" /> Print invoice
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
